import { flattenToPaths, partition, set } from '@exodus/basic-utils';
import { OrderSet, TxSet } from '@exodus/models';
import { getAssetSourceIncomingOrders } from '../redux/selectors/create-asset-source-incoming-orders.js';
import { getOrdersInProgress } from '../redux/selectors/orders-in-progress.js';
import { resultFunction as getPotentialCompleteOrders } from '../redux/selectors/potential-complete-orders.js';
import findOptimisticOrders from '@exodus/find-optimistic-orders';
import makeConcurrent, { byArguments as makeConcurrentByArguments } from 'make-concurrent';
import { getAllOrderInstances } from './utils.js';
const orderSetEqual = (a, b) => {
    if (a === b)
        return true;
    if (a.size !== b.size)
        return false;
    for (const order of a) {
        const bOrder = b.get(order.orderId);
        if (!bOrder || !order.equals(bOrder))
            return false;
    }
    return true;
};
const legacyOrdersChannel = {
    type: 'order',
    channelName: 'orders',
    batchInterval: 1000,
};
const ordersChannel = {
    type: 'order',
    channelName: 'v1_orders',
    batchInterval: 1000,
};
const concurrent = makeConcurrent;
const concurrentByArguments = makeConcurrentByArguments;
const updateWithOrders = (orderSet, orders) => {
    const [toUpdate, toAdd] = partition(orders, (order) => orderSet.has(order));
    const actuallyChanged = toUpdate.filter((order) => {
        const existing = orderSet.get(order);
        return !existing || !existing.equals(order);
    });
    let next = orderSet;
    if (actuallyChanged.length > 0)
        next = next.update(actuallyChanged);
    if (toAdd.length > 0)
        next = next.addBulk(toAdd);
    return next;
};
const findFailedOrders = ({ failedSentTxs, orders, }) => {
    const result = [];
    if (failedSentTxs.length > 0) {
        const failedSentTxsIds = new Set(failedSentTxs.map((tx) => tx.txId));
        orders.forEach((order) => {
            if (failedSentTxsIds.has(order.fromTxId)) {
                result.push(order.update({ status: 'failed-final' }));
            }
        });
    }
    return result;
};
class OrdersModule {
    #logger;
    #assetsModule;
    #channel;
    #fusion;
    #loadedPromise;
    #loadedResolve = () => { };
    #loaded = false;
    #optimisticActivityEnabled;
    #ordersAtom;
    #firstMissAtAtom;
    #ordersMonitor;
    #txLogsByAssetSource = Object.create(null);
    #txLogsAtom;
    #subscriptions = [];
    #totalSyncedFromFusion = 0;
    #batchCount = 0;
    constructor({ assetsModule, fusion, logger, config, ordersAtom, firstMissAtAtom, ordersMonitor, txLogsAtom, }) {
        this.#logger = logger;
        this.#assetsModule = assetsModule;
        this.#fusion = fusion;
        this.#optimisticActivityEnabled = config.optimisticActivityEnabled;
        this.#ordersAtom = ordersAtom;
        this.#firstMissAtAtom = firstMissAtAtom;
        this.#txLogsAtom = txLogsAtom;
        this.#loadedPromise = new Promise((resolve) => {
            this.#loadedResolve = () => {
                this.#loaded = true;
                resolve();
            };
        });
        this.#fusion.channel({
            ...legacyOrdersChannel,
            processBatch: this.#processBatch,
        });
        this.#channel = this.#fusion.channel({
            ...ordersChannel,
            processBatch: this.#processBatch,
        });
        this.#ordersMonitor = ordersMonitor;
    }
    #handleOrderBulkUpdate = async (changes) => {
        const updatedOrders = changes.map(({ order, changes: orderChanges }) => order.update(orderChanges));
        await this.upsert(updatedOrders);
    };
    #subscribeOnTxLogUpdate = () => {
        const unsubscribe = this.#txLogsAtom.observe(({ value, changes }) => {
            if (changes) {
                const paths = flattenToPaths(changes);
                paths.forEach(([walletAccount, assetName, txLog]) => this.#handleTxLogUpdate({ walletAccount, assetName, txLog }));
            }
            else {
                this.#txLogsByAssetSource = Object.assign(Object.create(null), value);
            }
        });
        this.#subscriptions.push(unsubscribe);
        return unsubscribe;
    };
    #handleTxLogUpdate = async ({ assetName, walletAccount, txLog, }) => {
        if (this.#loaded) {
            await this.#updateOptimisticOrders({ txLog, assetName, walletAccount }).catch((e) => {
                this.#logger.warn('failed to update optimistic orders', e);
            });
        }
        if (walletAccount in {} ||
            assetName in {} ||
            walletAccount === 'prototype' ||
            assetName === 'prototype') {
            this.#logger.warn('ignoring txLog update with prototype-like key', {
                walletAccount,
                assetName,
            });
            return;
        }
        set(this.#txLogsByAssetSource, [walletAccount, assetName], txLog);
    };
    #updateOptimisticOrders = concurrentByArguments(async ({ txLog, walletAccount, assetName }) => {
        const orderSet = await this.#ordersAtom.get();
        const ordersInProgress = getOrdersInProgress(orderSet);
        const potentialCompleteOrders = getPotentialCompleteOrders(orderSet);
        const preTxs = this.#txLogsByAssetSource[walletAccount]?.[assetName] || TxSet.EMPTY;
        let updatedOrders = [];
        const relatedOrders = [...ordersInProgress, ...potentialCompleteOrders].filter((order) => order.toWalletAccount === walletAccount ||
            order.toAsset === assetName ||
            order.fromWalletAccount === walletAccount ||
            order.fromAsset === assetName);
        const failedSentTxs = [...txLog].filter((tx) => {
            if (!tx.txId)
                return false;
            const isNew = !preTxs.get(tx.txId);
            return !isNew && tx.failed && tx.sent;
        });
        const incomingOrders = [
            ...getAssetSourceIncomingOrders({ orderSet: ordersInProgress, assetName, walletAccount }),
            ...getAssetSourceIncomingOrders({
                orderSet: potentialCompleteOrders,
                assetName,
                walletAccount,
            }),
        ].sort((a, b) => b.date.getTime() - a.date.getTime());
        const optimisticUpdatedOrders = findOptimisticOrders({
            incomingOrders,
            preTxs,
            postTxs: txLog,
        });
        const failedOrders = findFailedOrders({ failedSentTxs, orders: relatedOrders });
        updatedOrders = [...updatedOrders, ...failedOrders, ...optimisticUpdatedOrders];
        if (updatedOrders.length > 0) {
            await this.upsert(updatedOrders);
        }
    }, ([{ assetName }]) => assetName);
    #getChannel = async () => {
        await this.#channel.awaitProcessed();
        return this.#channel;
    };
    start = async () => {
        if (this.#optimisticActivityEnabled) {
            this.#subscribeOnTxLogUpdate();
        }
        this.#ordersMonitor.on('orders-bulk-update', this.#handleOrderBulkUpdate);
    };
    load = async () => {
        await this.#ordersAtom.get();
        this.#loadedResolve();
    };
    upsert = concurrent(async (orders) => {
        await this.#loadedPromise;
        const { value: txLogsByAssetSource } = await this.#txLogsAtom.get();
        const updatedOrders = orders.map((order) => {
            if (this.#optimisticActivityEnabled &&
                !order.hasOptimisticStatus &&
                order.exodusStatus === 'in-progress') {
                const toTxId = order.toTxId;
                const toWalletAccount = order.toWalletAccount;
                const toAsset = order.toAsset;
                const txLog = toWalletAccount && toAsset ? txLogsByAssetSource[toWalletAccount]?.[toAsset] : undefined;
                if (txLog && toTxId && txLog.get(toTxId)) {
                    return order.update({
                        status: 'optimistic-complete',
                    });
                }
            }
            return order;
        });
        await this.#ordersAtom.set((current) => {
            const next = updateWithOrders(current, updatedOrders);
            return orderSetEqual(current, next) ? current : next;
        });
        void this.#pushUnsyncedOrders().catch((err) => {
            this.#logger.error('failed to push unsynced orders to fusion', err);
        });
    });
    clear = async () => {
        await this.#ordersAtom.reset();
        await this.#firstMissAtAtom.reset();
        this.#logger.debug('cleared');
    };
    #pushUnsyncedOrders = concurrent(async () => {
        await this.#loadedPromise;
        const ordersSet = await this.#ordersAtom.get();
        const unsynced = [...ordersSet].filter((o) => !o.synced);
        if (unsynced.length === 0)
            return;
        const pushedById = new Map(unsynced.map((order) => [order.orderId, order]));
        this.#logger.log(`pushing ${unsynced.length} orders to fusion`);
        await this.#pushToFusion(unsynced);
        await this.#ordersAtom.set((current) => {
            const syncedOrders = [...current]
                .filter((order) => {
                const pushed = pushedById.get(order.orderId);
                return pushed !== undefined && !order.synced && pushed.equals(order);
            })
                .map((order) => order.update({ synced: true }));
            const next = updateWithOrders(current, syncedOrders);
            return orderSetEqual(current, next) ? current : next;
        });
    });
    #processBatch = async (ordersBatch) => {
        this.#batchCount++;
        this.#totalSyncedFromFusion += ordersBatch.length;
        this.#logger.debug(`fusion batch #${this.#batchCount}: received ${ordersBatch.length} orders (total received: ${this.#totalSyncedFromFusion})`);
        await this.#loadedPromise;
        const orders = ordersBatch.map((orderData) => orderData.data);
        const assets = this.#assetsModule.getAssets();
        const unexistingAssetNames = orders.reduce((acc, order) => {
            const fromAsset = order.fromAsset;
            const toAsset = order.toAsset;
            if (fromAsset && !assets[fromAsset]) {
                acc.add(fromAsset);
            }
            if (toAsset && !assets[toAsset]) {
                acc.add(toAsset);
            }
            return acc;
        }, new Set());
        if (unexistingAssetNames.size > 0) {
            await this.#assetsModule
                .addRemoteTokens({ tokenNames: [...unexistingAssetNames] })
                .catch((err) => {
                this.#logger.error(`failed to add unexisting assets ${[...unexistingAssetNames]} for orders`, err);
            });
        }
        const parsedOrderInstances = getAllOrderInstances({
            orders,
            assetsModule: this.#assetsModule,
            logger: this.#logger,
        });
        if (orders.length !== parsedOrderInstances.length) {
            this.#logger.debug(`fusion batch #${this.#batchCount}: ${parsedOrderInstances.length}/${orders.length} orders parseable on this platform`);
        }
        const ordersFromFusion = OrderSet.fromArray(parsedOrderInstances);
        const ordersSet = await this.#ordersAtom.get();
        const alreadySyncedUpOrders = [...ordersSet].filter((order) => ordersFromFusion.has(order));
        const latestSyncedDownOrders = [...ordersFromFusion].filter((order) => !ordersSet.has(order));
        const alreadySyncedNeedsMark = alreadySyncedUpOrders.filter((order) => !order.synced);
        const markedOrders = alreadySyncedNeedsMark.map((order) => order.update({ synced: true }));
        const batchedOrders = latestSyncedDownOrders.map((order) => order.update({ status: 'e2e-syncing', synced: true }));
        this.#logger.debug(`fusion batch #${this.#batchCount}: new=${batchedOrders.length} existing=${alreadySyncedUpOrders.length} needsSync=${alreadySyncedNeedsMark.length} atomTotal=${ordersSet.size + batchedOrders.length}`);
        const allChanges = [...markedOrders, ...batchedOrders];
        if (allChanges.length > 0) {
            await this.#ordersAtom.set((current) => {
                const next = updateWithOrders(current, allChanges);
                return orderSetEqual(current, next) ? current : next;
            });
        }
        if (batchedOrders.length > 0) {
            this.#ordersMonitor.tick();
        }
    };
    #pushToFusion = async (orders) => {
        const channel = await this.#getChannel();
        const pushToFusionPromises = orders.map((order) => channel.push({
            type: ordersChannel.type,
            data: order,
        }));
        await Promise.all(pushToFusionPromises);
        this.#logger.debug(`pushed ${orders.length} orders to fusion`);
    };
    stop = () => {
        this.#subscriptions.forEach((unsubscribe) => unsubscribe());
        this.#subscriptions = [];
        this.#ordersMonitor.stop();
    };
}
const createOrdersModule = (...args) => new OrdersModule(...args);
const ordersModuleDefinition = {
    id: 'orders',
    type: 'module',
    factory: createOrdersModule,
    dependencies: [
        'assetsModule',
        'config',
        'fusion',
        'logger',
        'ordersAtom',
        'firstMissAtAtom',
        'ordersMonitor',
        'txLogsAtom',
    ],
    public: true,
};
export default ordersModuleDefinition;
